import { Search, Plus, X, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import OptionType from "../../types/option-type";
import { ucFirst } from "../../lib/helper-support";

type onChangeInputFunc = (e: React.ChangeEvent<HTMLInputElement>) => void

const SearchableDropdown = ({
    options,
    onSelectedOption,
    placeholder,
    label = '',
    labelClass,
    value,
    error,
    disabled = false,
    onChangeInput,
    inputClass,
    required = true,
    createNewLabel,
    onCreateNew,
    showCreateNewWhenEmpty = true,
}: {
    options?: OptionType[],
    placeholder?: string,
    label?: string,
    labelClass?: string,
    value?: string | null,
    error?: string | null,
    disabled?: boolean,
    onChangeInput?: onChangeInputFunc,
    onSelectedOption?: (value: string | null) => void,
    inputClass?: string,
    required?: boolean,
    /** Text for the create-new action. Defaults to "Create as new {label}". */
    createNewLabel?: string,
    /** Called with the current search string when the user clicks the create-new option. */
    onCreateNew?: (inputValue: string) => void,
    /**
     * When true (default), the create-new option is shown whenever the dropdown
     * is open and `onCreateNew` is provided — even if options exist.
     * When false, it only appears when the options list is empty.
     */
    showCreateNewWhenEmpty?: boolean,
}) => {

    const [showMOptions, setShowMOptions] = useState<boolean>(false);
    const showMOptionsRef = useRef<HTMLDivElement>(null);
    const [getPlaceholder, setGetPlaceholder] = useState<string>(placeholder ?? `Select ${label}`);
    const [getValue, setGetValue] = useState<string>(value ?? '');
    const [initialState, setToggle] = useState<boolean>(true);

    const resolvedCreateNewLabel = createNewLabel ?? `Create as new ${label}`;

    // Determine whether to show the create-new row
    const hasOptions = options && options.length > 0;
    const showCreateNew =
        showMOptions &&
        !!onCreateNew &&
        (showCreateNewWhenEmpty ? true : !hasOptions);

    useEffect(() => {
        setGetValue(value ?? '');
        setGetPlaceholder(value ?? placeholder ?? `Select ${label}`);
    }, [value, placeholder, label]);

    useEffect(() => {
        if (!options || options.length === 0) {
            if (onSelectedOption) onSelectedOption(null);
            return;
        }
        if (!initialState) {
            setShowMOptions(true);
        }
    }, [options]);

    const onShowOption = useCallback((triggerBy = 'default') => {
        if (triggerBy === 'outsideEvent') {
            setShowMOptions(false);
        } else {
            setShowMOptions(prev => !prev);
        }
    }, []);

    const handleOnClickOutside = useCallback((event: MouseEvent | Event) => {
        if (showMOptionsRef.current && !showMOptionsRef.current.contains(event.target as Node)) {
            onShowOption('outsideEvent');
        }
    }, [onShowOption]);

    useEffect(() => {
        document.addEventListener('click', handleOnClickOutside);
        document.addEventListener('input', handleOnClickOutside);
        return () => {
            document.removeEventListener('click', handleOnClickOutside);
            document.removeEventListener('input', handleOnClickOutside);
        };
    }, [handleOnClickOutside]);

    const onSelectOption = (item: OptionType | null) => {
        if (!item) {
            setGetPlaceholder(placeholder ?? `Select ${label}`);
            setShowMOptions(false);
            if (onSelectedOption) onSelectedOption(null);
            setGetValue('');
            return;
        }
        if (onSelectedOption) onSelectedOption(item.value);
        setGetPlaceholder(item.name);
        setGetValue(item.name);
        setShowMOptions(false);
    };

    const handleCreateNew = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onCreateNew) onCreateNew(getValue);
        setShowMOptions(false);
    };
    
    const handleClear = () => {
        setGetValue('');
        if (onCreateNew) onCreateNew('');
        if (onSelectedOption) onSelectedOption(null);
        setGetPlaceholder(`${placeholder ?? `Select ${label}`}`);
    }

    const dropdownVisible = showMOptions && (hasOptions || showCreateNew);

    return (
        <div onClick={() => setShowMOptions(!showMOptions)} ref={showMOptionsRef} className="relative">
            {label && <p className={`${labelClass} flex items-center space-x-3 label mb-1`}>
                <span>{label}</span>
                {required && <span className="ml-1 text-red-500">*</span>}
            </p>}
            <div className="relative flex items-center">
                <div className="absolute z-10 left-5">
                    <Search size={18} />
                </div>
                <input
                    placeholder={getPlaceholder}
                    type='text'
                    className={`input pl-12 ${inputClass} border ${error && 'border-red-500'} ${disabled && 'text-light-black'}`}
                    onInput={onChangeInput}
                    value={getValue}
                    disabled={disabled}
                    inputMode={'search'}
                />
                {getValue ? <div onClick={handleClear} className="absolute z-10 right-5 cursor-pointer">
                    <X size={18} />
                </div> : <div onClick={handleClear} className="absolute z-10 right-5 cursor-pointer">
                    <ChevronDown size={18} />
                </div>}
                {dropdownVisible && (
                    <div className="absolute left-0 right-0 w-full bg-white h-[10em] overflow-y-auto z-50 rounded shadow-md border p-2 top-12">
                        <ul className="grid gap-y-2">
                            {hasOptions && (
                                <li
                                    onClick={() => onSelectOption(null)}
                                    className="p-1 cursor-pointer themeTextMuted hover:bg-gray-50"
                                >
                                    {placeholder ?  ucFirst(placeholder) : `Select ${label}`}
                                </li>
                            )}
                            {options?.map((option, index) => (
                                <li
                                    key={index}
                                    onClick={() => onSelectOption(option)}
                                    className="p-1 cursor-pointer hover:bg-gray-50"
                                >
                                    {ucFirst(option.name)}
                                </li>
                            ))}
                            {showCreateNew && (
                                <li
                                    onClick={handleCreateNew}
                                    className="p-1 cursor-pointer flex items-center gap-2 text-primary hover:bg-blue-50 mt-1 pt-2"
                                >
                                    <Plus size={15} />
                                    <span className="text-sm font-medium">
                                        {getValue
                                            ? `${resolvedCreateNewLabel}: "${getValue}"`
                                            : resolvedCreateNewLabel}
                                    </span>
                                </li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
            {error && <p className="my-1 text-[13px] themeTextError font-normal">{ucFirst(error)}</p>}
        </div>
    );
}

export default SearchableDropdown;